import { LuDownload, LuPencil, LuPlay, LuUpload } from "react-icons/lu";
import ListTableRegion from "../../common/ListTableRegion";
import { PlanStatus } from "../../common/constants";
import {
    canEditPlan,
    canStartPlan,
    formatHealthEdgePlanId,
    formatPlanCellValue,
    formatPlanDate,
    getPlanStatusTone,
} from "../../../../utils/planManager/planConfigurationUtils.js";


/**
 * Column headers for the Plans table, in display order.
 * @readonly
 * @type {ReadonlyArray<string>}
 */
export const PLAN_TABLE_HEADERS = Object.freeze([
    "Client Name",
    "Client ID",
    "Plan ID",
    "Plan start date",
    "Plan end date",
    "Plan Status",
    "Create plan",
    "Notification Status",
    "Create Notification",
]);

import "./MainContent.scss";

const PLANS_TABLE_PANEL_ID = "plans-table-panel";

const PLAN_TABLE_COLUMNS = PLAN_TABLE_HEADERS.map((header) => ({
    key: header,
    label: header,
}));

/**
 * Plans list table. Renders one table for the active tab's rows with plan and
 * notification action controls per row. Pure presentational — emits user intent
 * upward via callbacks; owns no state.
 *
 * @param {object} props
 * @param {Array<Object>} props.plans - Filtered rows for the active tab.
 * @param {boolean} props.shouldSliceHealthEdgePlanId - When true, Plan ID cells drop the first four characters.
 * @param {string | null} props.selectedClientName - Ephemeral selected-client label (below table).
 * @param {(row: Object) => void} props.onStartOrEditPlan - Start/Edit plan setup for a row.
 * @param {(row: Object) => void} props.onExportPlan - Export a row's plan (stub).
 * @param {(row: Object) => void} props.onImportPlan - Import into a row (file input or overwrite modal).
 * @param {(row: Object) => void} props.onStartOrEditNotification - Start/Edit notification for a row.
 * @param {(clientId: string) => void} props.onSelectClient - Sets the ephemeral selected client from a row.
 * @returns {import('react').JSX.Element}
 */
const MainContent = ({
    plans,
    shouldSliceHealthEdgePlanId,
    selectedClientName,
    onStartOrEditPlan,
    onExportPlan,
    onImportPlan,
    onStartOrEditNotification,
    onSelectClient,
}) => {
    const columnCount = PLAN_TABLE_HEADERS.length;

    /**
     * @param {Object} row
     * @returns {import('react').JSX.Element}
     */
    const renderPlanActions = (row) => {
        const isNotStarted = canStartPlan(row.status);

        return (
            <div className="plans-table__actions">
                {canStartPlan(row.status) && (
                    <button
                        type="button"
                        className="plans-table__action-btn plans-table__action-btn--start"
                        title="Start plan"
                        aria-label="Start plan"
                        onClick={() => onStartOrEditPlan(row)}
                    >
                        <LuPlay aria-hidden size={16} />
                    </button>
                )}
                {canEditPlan(row.status) && (
                    <button
                        type="button"
                        className="plans-table__action-btn plans-table__action-btn--edit"
                        title="Edit plan"
                        aria-label="Edit plan"
                        onClick={() => onStartOrEditPlan(row)}
                    >
                        <LuPencil aria-hidden size={16} />
                    </button>
                )}
                <button
                    type="button"
                    className="plans-table__action-btn"
                    title="Export plan"
                    aria-label="Export plan"
                    disabled={isNotStarted}
                    onClick={() => onExportPlan(row)}
                >
                    <LuDownload aria-hidden size={16} />
                </button>
                <button
                    type="button"
                    className="plans-table__action-btn"
                    title="Import plan"
                    aria-label="Import plan"
                    onClick={() => onImportPlan(row)}
                >
                    <LuUpload aria-hidden size={16} />
                </button>
            </div>
        );
    };

    /**
     * @param {Object} row
     * @returns {import('react').JSX.Element}
     */
    const renderNotificationActions = (row) => {
        const isEdit = Boolean(row.hasNotificationConfig);

        return (
            <div className="plans-table__actions">
                <button
                    type="button"
                    className={
                        isEdit
                            ? "plans-table__action-btn plans-table__action-btn--edit"
                            : "plans-table__action-btn plans-table__action-btn--start"
                    }
                    title={isEdit ? "Edit notification" : "Start notification"}
                    aria-label={
                        isEdit ? "Edit notification" : "Start notification"
                    }
                    onClick={() => onStartOrEditNotification(row)}
                >
                    {isEdit ? (
                        <LuPencil aria-hidden size={16} />
                    ) : (
                        <LuPlay aria-hidden size={16} />
                    )}
                </button>
            </div>
        );
    };

    return (
        <ListTableRegion
            id={PLANS_TABLE_PANEL_ID}
            role="tabpanel"
            tableClassName="plans-table"
            columnCount={columnCount}
            isEmpty={plans.length === 0}
            emptyMessage="No plans to display."
            columns={PLAN_TABLE_COLUMNS}
            footer={
                selectedClientName ? (
                    <div className="plans-table__selected-client">
                        <h2>{selectedClientName}</h2>
                    </div>
                ) : null
            }
        >
            {plans.map((row) => {
                const notificationStatusLabel = row.hasNotificationConfig
                    ? PlanStatus.COMPLETED
                    : PlanStatus.NOT_STARTED;

                return (
                    <tr key={row.id}>
                        <td>{formatPlanCellValue(row.clientName)}</td>
                        <td>
                            <span onClick={() => onSelectClient(row.clientId)}>
                                {formatPlanCellValue(row.clientId)}
                            </span>
                        </td>
                        <td>
                            {shouldSliceHealthEdgePlanId
                                ? formatHealthEdgePlanId(row.planId)
                                : formatPlanCellValue(row.planId)}
                        </td>
                        <td>{formatPlanDate(row.planStartDate)}</td>
                        <td>{formatPlanDate(row.planEndDate)}</td>
                        <td className="plans-table__status-cell">
                            <span
                                className={`plans-table__status plans-table__status--${getPlanStatusTone(
                                    row.status,
                                )}`}
                            >
                                {formatPlanCellValue(row.status)}
                            </span>
                        </td>
                        <td>{renderPlanActions(row)}</td>
                        <td className="plans-table__status-cell">
                            <span
                                className={`plans-table__status plans-table__status--${getPlanStatusTone(
                                    notificationStatusLabel,
                                )}`}
                            >
                                {notificationStatusLabel}
                            </span>
                        </td>
                        <td>{renderNotificationActions(row)}</td>
                    </tr>
                );
            })}
        </ListTableRegion>
    );
};

export default MainContent;
